import { NotificationsOptions } from '@kyvg/vue3-notification';
import { defineStore } from 'pinia';

import { ApiError } from '@/repositories/generated/core/ApiError';
import { notify } from '@/utils';

type TRootState = {
  isLoading: boolean;
  dialogMessage: string;
  badRequestError?: {
    error: {
      code: string;
      message: string;
    };
  };
};

export const useRootStore = defineStore('root', {
  state: (): TRootState => ({
    isLoading: false,
    dialogMessage: '',
    badRequestError: undefined,
  }),
  actions: {
    /**
     * notifyによる通知とローディング制御付きの非同期関数を実行する（利用想定：API x notifyの利用箇所）
     *
     * @param asyncFunc 非同期関数
     * @param enableLoading ローディング表示の有効化
     * @param errorOptions 失敗時の通知オプション（typeのデフォルトはerror、省略時は通知なし）
     * @param successOptions 成功時の通知オプション（typeのデフォルトはinfo、省略時は通知なし）
     * @returns 非同期関数の実行結果
     */
    async executeWithNotifyAndLoading<T>(
      asyncFunc: () => Promise<T>,
      enableLoading: boolean,
      errorOptions?: NotificationsOptions,
      successOptions?: NotificationsOptions,
    ): Promise<T> {
      if (enableLoading) this.isLoading = true;
      return asyncFunc()
        .then((result) => {
          if (successOptions) {
            notify({
              ...successOptions,
              type: successOptions.type ?? 'info',
            });
          }
          return result;
        })
        .catch((e: unknown) => {
          if (errorOptions) {
            const errorType = errorOptions.type ?? 'error';

            // APIエラーは、HTTPステータス400以外はaxiosのinterceptorで制御しているため、
            // ここでは400のみを対象とする（例：404画面に遷移 + notifyで失敗メッセージ を表示しないため）
            // それ以外のエラーは、指定した失敗メッセージで通知する（例：undefinedエラー等）
            if (e instanceof ApiError) {
              if (e.status === 400) {
                notify({
                  ...errorOptions,
                  type: errorType,
                  text: this.badRequestError?.error.message ?? errorOptions.text,
                });
              }
            } else {
              notify({
                ...errorOptions,
                type: errorType,
                text: errorOptions.text,
              });
            }
          }
          throw e;
        })
        .finally(() => {
          if (enableLoading) this.isLoading = false;
        });
    },
  },
});
